import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import {
  Button,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSet,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Switch
} from '@@/ui/primitives';
import { XIcon, PlusIcon } from 'lucide-react';

export function PickListConstraints() {
  const form = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'definition.options'
  });

  return (
    <div className='flex flex-col gap-6 p-4'>
      <div className='flex flex-col gap-1'>
        <h4 className='text-sm font-semibold text-neutral-950'>Pick List configuration</h4>
        <p className='text-xs text-neutral-600'>
          Configure the available options and selection behavior for this pick list field.
        </p>
      </div>
      <div className='flex flex-col'>
        <FieldSet className='gap-1.5 p-0! max-w-sm'>
          <FieldLabel htmlFor='picklist-options'>Options</FieldLabel>
          <FieldDescription>You can add up to 128 options here.</FieldDescription>
          <FieldGroup className='gap-2'>
            {fields.map((field, index) => (
              <Controller
                key={field.id}
                name={`definition.options.${index}`}
                control={form.control}
                render={({ field: controllerField, fieldState }) => (
                  <Field orientation='horizontal' data-invalid={fieldState.invalid}>
                    <FieldContent>
                      <InputGroup className='bg-white shadow-none'>
                        <InputGroupInput
                          {...controllerField}
                          id={`form-rhf-array-item-${index}`}
                          aria-invalid={fieldState.invalid}
                          type='text'
                          className='text-sm! font-medium!'
                          autoComplete='off'
                        />
                        {fields.length > 1 && (
                          <InputGroupAddon align='inline-end'>
                            <InputGroupButton
                              type='button'
                              variant='ghost'
                              size='icon-xs'
                              onClick={() => remove(index)}
                              aria-label={`Remove option ${index + 1}`}
                            >
                              <XIcon />
                            </InputGroupButton>
                          </InputGroupAddon>
                        )}
                      </InputGroup>
                      {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                    </FieldContent>
                  </Field>
                )}
              />
            ))}
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='text-sm! font-medium! self-start'
              onClick={() => append('')}
              disabled={fields.length >= 128}
            >
              <PlusIcon className='size-4' />
              Option
            </Button>
          </FieldGroup>
          {form.formState.errors.root && <FieldError errors={[form.formState.errors.root]} />}
        </FieldSet>
        <FieldSet className='mt-4 p-0! max-w-sm'>
          <FieldGroup>
            <Controller
              name='definition.multiple'
              defaultValue={false}
              control={form.control}
              render={({ field, fieldState }) => (
                <Field orientation='horizontal' data-invalid={fieldState.invalid}>
                  <FieldContent>
                    <FieldLabel htmlFor='form-rhf-switch-multiple'>Allow multiple selections</FieldLabel>
                    <FieldDescription>
                      Enable this option to allow users to select more than one option from the pick list.
                    </FieldDescription>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </FieldContent>
                  <Switch
                    id='form-rhf-switch-multiple'
                    name={field.name}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-invalid={fieldState.invalid}
                  />
                </Field>
              )}
            />
          </FieldGroup>
        </FieldSet>
      </div>
    </div>
  );
}
